<script setup>
import { Icon, Dropdown } from "@components";
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useService } from "@store/service";
const $service = useService();

import { useSettings } from "@store/settings";
const $settings = useSettings();

import { getPrice, showDurationUnit } from "@utils";

const props = defineProps({
	service: {
		type: Object,
	},
	index: {
		type: Number,
	},
	length: {
		type: Number,
	},
});

const service = computed(() => (props.service ? props.service : {}));
const router = useRouter();

// On click row.
const goToEdit = () => {
	if (!access("service.add")) return;
	router.push({
		name: "service.edit",
		params: {
			serviceId: service.value.service_id,
		},
	});
};

const row = ref(null);
const openingEvent = (e) => {
	// Bail, if [no-events] attribute is present in e.target.
	if (
		!row.value ||
		!row.value.contains(e.target) ||
		e.target.closest("[no-events]")
	)
		return;
	e.preventDefault();
	goToEdit();
};
onMounted(() => {
	document.addEventListener("click", openingEvent);
});

onUnmounted(() => {
	document.removeEventListener("click", openingEvent);
});

const updateServiceStatus = async (id, status) => {
	let service = $service.getServices.find(
		(service) => service.service_id == id
	);
	service.status = status;

	const res = await $service.updateService(service, false);

	if (res.success) {
		if (status == "archive") {
			Toast.success(
				`${String($settings.labels.service?.singular)} archived successfully`
			);
		} else if (status == "publish") {
			Toast.success(
				`${String(
					$settings.labels.service?.singular
				)} restored and published successfully`
			);
		}
	} else {
		Toast.error(res.message);
	}
};

const archiveService = async (id) => {
	let confirm = await Prompt.fire({
		title: `Archive ${String($settings.labels.service?.singular)}`,
		message: `Are you sure you want to archive this ${String(
			$settings.labels.service?.singular
		).toLowerCase()}?`,
		ok: "Yes, archive",
		cancel: "No, cancel",
	});
	if (!confirm.ok) return;

	await updateServiceStatus(id, "archive");
};

const publishService = async (id) => {
	await updateServiceStatus(id, "publish");
};

const showPrice = (type, price) => {
	if (type == "fixed") {
		return getPrice(price);
	} else if (type == "free") {
		return "Free";
	} else if (type == "none") {
		return "N/A";
	}
};
</script>

<template>
	<!-- <div v-bind="$attrs"> -->
	<!-- <div ref="row" class="h-[72px] border border-[#D0D5DD] border-l-[3px] cursor-pointer hover:bg-[#F9FAFB] pl-7 w-full flex items-center justify-between py-6 px-4 relative" :style="`border-left-color: ${service.meta.color} !important;`" :class="{ 'rounded-tr-lg': props.index == 0 }, { 'border-b-0  rounded-b-none ': props.index == 0 && props.index + 1 !== props.length }, { 'rounded-b-lg rounded-bl-none': props.index + 1 == props.length }"> -->
	<div :data-service="service.service_id" ref="row" :class="{ 'cursor-pointer': access('service.add') }" class="h-[72px] border border-[#D0D5DD] border-l-[3px] transition-all hover:bg-[#F9FAFB] pl-7 w-full flex items-center justify-between py-6 px-4 relative border-b-0 last:border-b first:rounded-tr-lg last:rounded-br-lg" :style="`border-left-color: ${service.meta.color} !important;`">
		<div class="text-[#101828] text-base font-semibold flex items-center gap-3 whitespace-nowrap" :class="service.status == 'publish' ? 'text-[#101828]' : 'text-[#98A2B3]'">
			<div>{{ service.name || 'Untitled Service' }}</div>
			<span class="text-sm font-medium ml-3" :class="service.status == 'publish' ? 'text-[#667085]' : 'text-[#98A2B3]'">#{{ service.service_id }}</span>
			<div class="bg-[#F2F4F7] text-xs font-medium px-2 py-0.5 rounded-2xl capitalize" v-if="service.status !== 'publish'">{{ service.status }}</div>
			<div class="bg-[#F2F4F7] text-xs font-medium px-2 py-0.5 rounded-2xl capitalize" v-if="service.meta.is_private">Private</div>
		</div>
		<div class="flex items-center gap-4 text-[rgb(152,162,179)] text-sm font-semibold">
			<div>{{ showDurationUnit(service.meta.duration, service.meta.duration_unit) }}</div>
			<div>{{ showPrice(service.meta.price_type, service.meta.price) }}</div>
			<Dropdown v-if="access('service.add')" :contentClass="`w-56 top-9 w-[117px] ${$settings.isRTL ? ' left-0' : 'right-0'}`" no-events>
				<Icon name="dotsVertical" width="32px" height="26px" class="cursor-pointer text-[#98A2B3] transition-all hover:text-[#475467] hover:bg-[#EAECF0] rounded py-[1px] px-1" />
				<template #content>
					<div class="flex flex-col gap-0.5 px-1.5 py-1">
						<div class="transition rounded-md">
							<div class="px-2.5 py-[9px] flex gap-2 items-center transition-all hover:bg-[#F9FAFB] rounded-md cursor-pointer" @click.prevent="goToEdit">
								<Icon size="16px" name="edit05" />
								<div class="text-[#344054] text-sm font-medium">Edit</div>
							</div>
							<div :title="service.status == 'draft' ? 'Cannot publish/archive a draft service' : ''" @click="service.status == 'draft' ? '' : (service.status == 'publish' ? archiveService(service.service_id) : publishService(service.service_id))" class="px-2.5 py-[9px] flex gap-2 items-center transition-all hover:bg-[#F9FAFB] rounded-md" :class="service.status == 'draft' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'">
								<Icon size="16px" :name="service.status == 'publish' ? 'archive' : 'check'" />
								<div class="text-[#344054] text-sm font-medium">{{ service.status == 'publish' ? 'Archive' : 'Publish' }}</div>
							</div>
							<div @click="$service.deleteService(service.service_id)" class="px-2.5 py-[9px] flex gap-2 items-center transition-all hover:bg-[#F9FAFB] rounded-md cursor-pointer">
								<Icon size="16px" name="trash01" />
								<div class="text-[#344054] text-sm font-medium">Delete</div>
							</div>
						</div>
					</div>
				</template>
			</Dropdown>
		</div>
	</div>
	<!-- <slot></slot> -->
	<!-- </div> -->
</template>